import CircleIcon from '@mui/icons-material/Circle';
import {
  Typography,
  Box,
  Button,
  Stack,
  useTheme,
  Divider,
} from '@mui/material';
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { theme } from '@app/theme';
import { ProductFeature } from '@app/types/catalog';

interface StrengthsSectionProps {
  features: ProductFeature[];
  showReadMore?: boolean;
  strengthsDescription: string;
  strengthsTitle: string;
}

interface ReadMoreProps {
  maxLines?: number;
  text: string;
}

interface OrganizationStrengthsSectionProps {
  features: string[];
  showReadMore?: boolean;
  strengthsDescription: string;
  strengthsTitle: string;
}

function ReadMore({ text, maxLines = 2 }: ReadMoreProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRef.current) {
      const lineHeight = parseFloat(
        window.getComputedStyle(textRef.current).lineHeight
      );
      const maxHeight = lineHeight * maxLines;
      if (textRef.current.scrollHeight > maxHeight) {
        setIsTruncated(true);
      }
    }
  }, [maxLines, text]);

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div>
      <Box
        ref={textRef}
        sx={{
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: isExpanded ? 'none' : maxLines,
          display: '-webkit-box',
          overflow: 'hidden',
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontSize: 16, mt: 2, whiteSpace: 'pre-wrap' }}
        >
          {text}
        </Typography>
      </Box>
      {isTruncated && (
        <Button
          onClick={handleToggle}
          sx={{
            color: '#8C8C8C',
            fontWeight: 300,
            p: 0,
            textDecoration: 'underline',
          }}
        >
          {isExpanded ? '表示を折りたたむ' : '続きを読む'}
        </Button>
      )}
    </div>
  );
}

export function StrengthsSection({
  features,
  strengthsTitle,
  strengthsDescription,
  showReadMore = true,
}: StrengthsSectionProps): ReactElement {
  return (
    <Box sx={{ width: 1 }}>
      <Typography
        alignItems="center"
        bgcolor={theme.palette.blueAccent.main}
        color="white"
        px={1}
        py={0.5}
        fontWeight={600}
      >
        物件の特徴
      </Typography>
      <Stack mt={1}>
        {features
          .filter((feature) => feature.value)
          .map((feature, index) => (
            <Stack alignItems="flex-start" my={0.5} key={index}>
              <Typography variant="body1" fontWeight="bold">
                {feature.value}
              </Typography>
            </Stack>
          ))}
      </Stack>
      <Stack flexDirection="column" pl={1}>
        <Typography variant="body1" sx={{ mt: 3 }}>
          {strengthsTitle}
        </Typography>
        {showReadMore ? (
          <ReadMore text={strengthsDescription} />
        ) : (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {strengthsDescription}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export function OrganizationStrengthsSection({
  features = [],
  strengthsTitle,
  strengthsDescription,
  showReadMore = true,
}: OrganizationStrengthsSectionProps): ReactElement {
  const theme = useTheme();
  const filteredFeatures = useMemo(() => {
    return features.filter((feature) => feature);
  }, [features]);
  return (
    <Stack
      direction={{ sm: 'row', xs: 'column' }}
      spacing={3}
      sx={{ pt: 4, width: '100%' }}
      divider={
        <Divider
          sx={{
            border: `solid 1px ${theme.palette.divider}`,
            display: { sm: 'block ', xs: 'none' },
            mx: { sm: 3, xs: 0 },
            my: { sm: 0, xs: 3 },
          }}
        />
      }
    >
      <Stack direction="column" py={1} spacing={2} width="100%">
        {filteredFeatures.map((feature, index) => (
          <React.Fragment key={index}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CircleIcon sx={{ color: '#00a0ff', fontSize: 16 }} />
              <Typography
                variant="body2"
                sx={{ fontSize: 16, fontWeight: 'bold' }}
              >
                {feature}
              </Typography>
            </Stack>
            <Divider
              sx={{
                borderStyle: 'dashed',
              }}
            />
          </React.Fragment>
        ))}
      </Stack>
      <Box
        width="100%"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          minHeight: 100,
        }}
      >
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: theme.palette.blueAccent.main, fontSize: 18, mb: 2 }}
        >
          {strengthsTitle}
        </Typography>
        {showReadMore ? (
          <ReadMore text={strengthsDescription} />
        ) : (
          <Typography
            variant="body2"
            sx={{ fontSize: 16, mt: 2, whiteSpace: 'pre-wrap' }}
          >
            {strengthsDescription}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
